import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Add credentials to all requests to include cookies.
    // Do not set Content-Type for FormData (browser sets multipart boundary), GET/HEAD, or
    // blob/arraybuffer downloads (forcing application/json on GET confuses proxies).
    const isFormData = req.body instanceof FormData;
    const method = (req.method || 'GET').toUpperCase();
    const isGetOrHead = method === 'GET' || method === 'HEAD';
    const isBlob = req.responseType === 'blob' || req.responseType === 'arraybuffer';
    const authReq = req.clone({
      ...(isFormData || isGetOrHead || isBlob ? {} : { setHeaders: { 'Content-Type': 'application/json' } }),
      withCredentials: true
    });

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(() => error);
        }
        // Public auth endpoints (login/register/forgot-password/reset-password/logout) and
        // /refresh itself must not trigger recursive refresh or force logout on 401.
        if (this.isPublicAuthOrRefreshUrl(req.url)) {
          return throwError(() => error);
        }
        // Deduplicated refresh: only force-logout when refresh token is definitively rejected (401/403).
        // Transient network / 5xx blips during refresh keep the user logged in.
        return this.authService.refreshTokenWithOutcome().pipe(
          switchMap(outcome => {
            if (outcome === 'refreshed') {
              return next.handle(authReq);
            }
            if (outcome === 'unauthorized') {
              this.authService.forceLogout();
              this.router.navigate(['/auth/login']);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private isPublicAuthOrRefreshUrl(url?: string | null): boolean {
    if (!url) return false;
    const clean = url.split('?')[0].toLowerCase();
    return (
      clean.endsWith('/refresh') ||
      clean.endsWith('/login') ||
      clean.endsWith('/register') ||
      clean.endsWith('/forgot-password') ||
      clean.endsWith('/reset-password') ||
      clean.endsWith('/logout')
    );
  }
}